import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { BodyText, Icon, Link, List, ModalDialog, Span } from '@gaiads/telia-react-component-library';
import { AdminCategory, AdminSubcategory } from '@cdaas/api-clients/dist/product-service-openapi';

import { updateOneAdminCategory } from '../../../../store/adminCategories';
import { setNotification } from '../../../../store/ui';
import { AdminSettingsErrorProps } from '../../AdminSettings';
import SubcategoryItem from '../SubcategoryItem/SubcategoryItem';
import { productServiceAdminCategoriesClient } from '../../../../api-client/product-service-client';

interface SubcategoriesListProps extends AdminSettingsErrorProps {
  currentCategory?: AdminCategory;
  newSubcategory?: AdminSubcategory;
  handleSubcategoryAddition: () => void;
  handleNewSubcategoryBlur: () => void;
}

export default function SubcategoriesList(props: SubcategoriesListProps) {
  const { currentCategory, newSubcategory, errors, setErrors, handleSubcategoryAddition, handleNewSubcategoryBlur } = props;

  const dispatch = useDispatch();

  const [categoryToDelete, setCategoryToDelete] = useState<AdminCategory>();
  const [subcategoryIdToDelete, setSubcategoryIdToDelete] = useState<AdminSubcategory['id']>();

  const { t } = useTranslation('adminSettings');

  const handleSubcategorySave = async (category: AdminCategory): Promise<boolean> => {
    try {
      const response = await productServiceAdminCategoriesClient.putAdminCategory(category.id, category);
      dispatch(updateOneAdminCategory(response.data));
      return true;
    } catch {
      dispatch(
        setNotification({
          text: t('productCategories.errors.updateSubcategory'),
          show: true,
          type: 'warning',
        }),
      );
      return false;
    }
  };
  const handleNewSubcategorySave = async (category: AdminCategory): Promise<boolean> => {
    try {
      const response = await productServiceAdminCategoriesClient.putAdminCategory(category.id, category);
      dispatch(updateOneAdminCategory(response.data));
      handleNewSubcategoryBlur();
      return true;
    } catch {
      dispatch(
        setNotification({
          text: t('productCategories.errors.createSubcategory'),
          show: true,
          type: 'warning',
        }),
      );
      return false;
    }
  };
  const handleDelete = (category: AdminCategory, subcategory: AdminSubcategory) => {
    setCategoryToDelete(category);
    setSubcategoryIdToDelete(subcategory.id);
  };
  const handleDeleteConfirmation = async (category: AdminCategory, subcategoryId: AdminSubcategory['id']) => {
    const subcategories: AdminCategory['subcategories'] = category.subcategories?.filter(
      categorySubcategory => categorySubcategory.id !== subcategoryId,
    );
    const updatedCategory: AdminCategory = {
      ...category,
      subcategories,
    };
    try {
      const response = await productServiceAdminCategoriesClient.putAdminCategory(category.id, updatedCategory);
      dispatch(updateOneAdminCategory(response.data));
      setCategoryToDelete(undefined);
      setSubcategoryIdToDelete(undefined);
    } catch {
      dispatch(
        setNotification({
          text: t('productCategories.errors.deleteSubcategory'),
          show: true,
          type: 'warning',
        }),
      );
    }
  };

  return (
    <>
      <List className="product-categories__category-list" flexItem>
        {currentCategory && [
          currentCategory.subcategories?.map(subcategory => (
            <SubcategoryItem
              key={`subcategory-${subcategory.id}`}
              category={currentCategory}
              subcategory={subcategory}
              errors={errors}
              setErrors={setErrors}
              handleSubcategorySave={handleSubcategorySave}
              handleDelete={handleDelete}
            />
          )),
          newSubcategory && (
            <SubcategoryItem
              key="new-subcategory"
              category={currentCategory}
              subcategory={newSubcategory}
              errors={errors}
              setErrors={setErrors}
              handleNewSubcategorySave={handleNewSubcategorySave}
              handleBlur={handleNewSubcategoryBlur}
              isNew={true}
            />
          ),
          <List.Item key="subcategory-add" className="product-categories__category-item product-categories__add">
            <Span onClick={() => handleSubcategoryAddition()}>
              <Link>
                <Icon name="plus" size="sm" alignTextToCenter className="product-categories__add-icon" />
                {t('productCategories.links.addSubcategory')}
              </Link>
            </Span>
          </List.Item>,
        ]}
      </List>
      <ModalDialog
        title={t('productCategories.deleteSubcategoryConfirmationDialog.title')}
        closeButtonText={t('productCategories.deleteSubcategoryConfirmationDialog.buttons.cancel')}
        acceptButtonText={t('productCategories.deleteSubcategoryConfirmationDialog.buttons.delete')}
        isOpen={!!subcategoryIdToDelete}
        onCloseClick={() => setSubcategoryIdToDelete(undefined)}
        onAcceptClick={() => categoryToDelete && subcategoryIdToDelete && handleDeleteConfirmation(categoryToDelete, subcategoryIdToDelete)}
      >
        <BodyText>{t('productCategories.deleteSubcategoryConfirmationDialog.content')}</BodyText>
      </ModalDialog>
    </>
  );
}
